import React, { useRef, useState } from "react";
import { Typography } from "@vds/typography";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "@vds/inputs";
import { DropdownSelect } from "@vds/selects";
import { Line } from "@vds/lines";
import "../bulkrule.css";
import { transformPolygonData } from "../../../global/utility/roiUtils";
import DisplayCanvas from "../../../polygonLib/displayPolygonLib/displayCanvas";
import { Icon } from "@vds/icons";
import SetTrigger from "./SetTrigger";
import {
  base64ToBlobUrl,
  ImageDownloadMedia,
} from "../../../model/mediaService";
import { Loader } from "@vds/loaders";
import { updateRuleValue } from "../../../store/actions";

function RuleReview() {
  const dispatch = useDispatch();
  const {
    ruleName,
    appSelected: { name: appName },
    siteSelected: { name: siteName },
    roiSelected,
    roiImages,
    areaSelected: { name: areaName },
    subAreaSelected: { name: subAreaName },
    floorSelected,
    siteSelected,
    selectedLocation,
    areaSelected,
  } = useSelector((state) => state.ruleReducer);
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const { fovList } = useSelector((state) => state?.deviceReducer);
  const pairedImages = useSelector((state) => state.imagesReducer.streamImages);
  const [imageCache, setImageCache] = useState({}); //for storing downloaded images
  const localCache = useRef({
    current: new Map(),
    ongoingRequests: new Map(),
  });
  const [imageLoading, setImageLoading] = useState(false);

  let locationName = "";
  switch (selectedLocation) {
    case "area":
      locationName = areaName;
      break;
    case "sub-area":
      locationName = subAreaName;
      break;
    case "floor":
      locationName = floorSelected?.name;
      break;
    case "site":
      locationName = siteSelected?.name;
      break;
  }
  //for downloading fov snapshots per each roi if selected entry-exit(floor or site)
  const fetchFloorImage = async (fovId, imageId) => {
    try {
      // Check if image already exists in cache
      if (localCache.current.current.has(fovId)) {
        return localCache.current.current.get(fovId);
      }

      // Check if a request for this fovId is already ongoing
      if (localCache.current.ongoingRequests.has(fovId)) {
        return localCache.current.ongoingRequests.get(fovId);
      }
      const requestPromise = ImageDownloadMedia(imageId, activeSession)
        .then((url) => {
          localCache.current.current.set(fovId, url);
          const updatedCache = { ...imageCache, [fovId]: url };
          setImageCache(updatedCache);
          if (updatedCache) {
            dispatch(
              updateRuleValue({
                propName: "roiImages",
                value: updatedCache,
              })
            );
          }
          localCache.current.ongoingRequests.delete(fovId);
          setImageLoading(false);
          return url;
        })
        .catch((error) => {
          console.error("Error downloading image:1", error);
          localCache.current.current.set(fovId, null);
          localCache.current.ongoingRequests.delete(fovId);
          setImageLoading(false);
        });

      // Storing the ongoing request
      localCache.current.ongoingRequests.set(fovId, requestPromise);
      setImageLoading(true);
      // Return the promise for the ongoing request
      //console.log({ requestPromise });
      return requestPromise;
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };
  return (
    <div className="create-rule-common-container conatiner-scroll-y">
      {imageLoading ? <Loader active fullscreen={false} surface="light" /> : ""}
      {/* --------------- Select App & Site ---------------- */}
      <div className="full-width">
        <div>
          <Typography className="bulk-rule-content-header-text">
            Review
          </Typography>
        </div>

        <div className="appAndSite-InputWrapper top-margin-10">
          <Input label="Alert name" defaultValue={ruleName} readOnly={true} />
        </div>

        <div className="appAndSiteDropdown-Wrapper bottom-margin-15 top-margin-10">
          <DropdownSelect label="App" readOnly={true}>
            <option>{appName}</option>
          </DropdownSelect>

          <DropdownSelect label="Site" readOnly={true}>
            <option>{siteName}</option>
          </DropdownSelect>
        </div>
        <div>
          <Line type="secondary" />
        </div>
      </div>

      {/* --------------- ROI ---------------- */}
      <div className="full-width">
        <div>
          <Typography
            className={`sasp_font-size-16 sasp_font-weight-700 sasp_read-text-color`}
          >
            Region(s) of interest
          </Typography>
          <div className="sel-roi-content">
            <Typography className={`sasp_font-size-16 sasp_font-weight-400`}>
              {locationName ? locationName : areaSelected?.name}
            </Typography>
          </div>

          <div className="sel-roi-image-grid bottom-margin-20">
            {/* checking if pairedmages is there and using them */}
            {roiSelected?.length > 0 &&
              roiSelected?.map((roiItem, index) => {
                const fovId = roiItem?.parentdeviceid;
                const pairedImage =
                  pairedImages && pairedImages.find((img) => img?.id === fovId);
                let imgUrl = null;
                if (pairedImage) {
                  imgUrl = pairedImage?.imageUrl;
                } else if (roiImages && roiImages[fovId]) {
                  imgUrl = roiImages[fovId];
                } else if (!localCache.current.current.has(fovId)) {
                  const fovDetails =
                    fovList && fovList?.find((fov) => fov.id === fovId);
                  if (fovDetails) {
                    fetchFloorImage(
                      fovId,
                      fovDetails?.customdata?.imageid
                    ).then((url) => {
                      if (url) {
                        imgUrl = url;
                      }
                    });
                  }
                } else {
                  imgUrl = localCache.current.current.get(fovId);
                }
                return (
                  <div className="image-block" key={`img_blk_${index}`}>
                    <div>
                      <DisplayCanvas
                        videoSource={imgUrl || ""}
                        imageSize={{ width: 200, height: 128 }}
                        initialPolygons={transformPolygonData(
                          roiSelected?.customdata
                            ? roiItem?.customdata
                            : roiItem?.customdata
                        )}
                        originalSize={[
                          roiItem?.customdata,
                          roiItem?.customdata,
                        ]}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <Line type="secondary" />
      </div>

      {/* --------------- Condition ---------------- */}
      <SetTrigger isReadOnly={true} />
    </div>
  );
}

export default RuleReview;
